import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {ZodSchema} from 'zod';

import {useCallback, useEffect, useRef, useState} from 'react';

import {omit} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  ConditionEnum,
  useGetVehicleQuery,
  useRegisterVehicleForSaleMutation,
  useUpdateVehicleMutation,
  VehicleTypeEnum,
} from '@dms/api/vehicle';
import {
  VehicleForm as VFKit,
  toAPIVehicle,
  SaleVehicleValidationSchema,
  SaleVehicle,
  useGetCarFeatures,
  useAdditionalNote,
  useFormNavigate,
  toBaseVehicle,
  useVehicleDocumentSync,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {
  formatDocumentTitle,
  handleApiError,
  useMandatoryFields,
  FieldAvailabilityContext,
} from '@dms/shared';
import {AuditDataAssetsV2Body} from '@dms/teas';

import {useRequiredParams} from 'shared/web';

import {SaleVehicleForm} from '../components/SaleVehicleForm';
import {DEFAULT_VEHICLE_TYPE} from '../constants/vehicleConstants';
import {useConditionDataSubmit} from '../hooks/useConditionDataSubmit';
import {useCreatePurchaseVehicle} from '../hooks/useCreatePurchaseVehicle';

const getTargetAlbum = (
  targetAlbum: unknown
): 'PHOTOS_CAR_SALES' | 'PHOTOS_CAR_PURCHASE' | null => {
  if (Array.isArray(targetAlbum) && targetAlbum.length > 0) {
    const value = targetAlbum[0];
    if (value === 'PHOTOS_CAR_SALES' || value === 'PHOTOS_CAR_PURCHASE') {
      return value;
    }
  }
  return 'PHOTOS_CAR_PURCHASE';
};

const buildRegistrationPayload = (values: SaleVehicle) => ({
  salePresetId: values.salePresetId,
  vehicleSourceCodeId: values.vehicleSourceCodeId,
  photos: values.photos?.map(({fileId}) => ({fileId})) ?? [],
  targetAlbum: values.photos?.length ? getTargetAlbum(values.targetAlbum) : null,
});

export function CreateSaleVehicleFromExisting() {
  const {navigateOnConfirm, navigateOnDiscard, isBusinessCaseReferrer} = useFormNavigate();

  const {id: vehicleId} = useRequiredParams();

  const {
    generateSchema,
    isMandatoryFieldsEnabled,
    isLoading: isMandatoryFieldsLoading,
  } = useMandatoryFields();

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );

  const currentContextRef = useRef<FieldAvailabilityContext>({
    vehicleType: (vehicle?.type as VehicleTypeEnum) ?? DEFAULT_VEHICLE_TYPE,
  });

  const [formSchema, setFormSchema] = useState<ZodSchema<SaleVehicle>>(() =>
    generateSchema(
      SaleVehicleValidationSchema,
      currentContextRef.current,
      (vehicle?.condition as ConditionEnum) ?? undefined
    )
  );

  useEffect(() => {
    if (vehicle) {
      currentContextRef.current = {
        ...currentContextRef.current,
        vehicleType: (vehicle.type as VehicleTypeEnum) ?? DEFAULT_VEHICLE_TYPE,
      };
      setFormSchema(
        generateSchema(
          SaleVehicleValidationSchema,
          currentContextRef.current,
          (vehicle.condition as ConditionEnum) ?? undefined
        )
      );
    }
  }, [vehicle?.type, vehicle?.condition, generateSchema]);

  const [getCarFeatures] = useGetCarFeatures();

  const [updateVehicleMutation, {isLoading: isUpdating}] = useUpdateVehicleMutation();
  const [registerVehicleForSaleMutation, {isLoading: isRegistering}] =
    useRegisterVehicleForSaleMutation();
  const {attachToBusinessCase} = useCreatePurchaseVehicle();

  const {handleSubmit: handleConditionDataSubmit} = useConditionDataSubmit();

  const {handleSaveNote} = useAdditionalNote();
  const {syncDocuments} = useVehicleDocumentSync();

  const handleVehicleTypeChange = useCallback(
    (type: VehicleTypeEnum, condition?: ConditionEnum) => {
      currentContextRef.current = {...currentContextRef.current, vehicleType: type};
      const newSchema = generateSchema(
        SaleVehicleValidationSchema,
        currentContextRef.current,
        condition
      );
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleFuelTypeChange = useCallback(
    (fuelTypeCondition: FieldAvailabilityContext) => {
      currentContextRef.current = {...currentContextRef.current, ...fuelTypeCondition};
      const newSchema = generateSchema(SaleVehicleValidationSchema, currentContextRef.current);
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleSave = async (values: SaleVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const existingFeatures = vehicle?.features ?? null;
    const apiPayload = toAPIVehicle(values, existingFeatures, sourcingData);

    const onSave = () =>
      updateVehicleMutation({
        vehicleId,
        updateVehicleRequestBody: apiPayload,
        validateMandatoryFields: isMandatoryFieldsEnabled(values.condition?.[0] ?? undefined),
      })
        .unwrap()
        // Register the vehicle for sale
        .then(async () => {
          const registrationPayload = buildRegistrationPayload(values);
          await registerVehicleForSaleMutation({
            vehicleId,
            registerVehicleForSaleV2RequestBody: registrationPayload,
          }).unwrap();
        })
        // Handle additional note
        .then(async () => {
          if (values.additionalNote) {
            await handleSaveNote(vehicleId, values.additionalNote);
          }
        })
        // Handle condition data
        .then(async () => {
          await handleConditionDataSubmit(
            vehicleId,
            omit(['sessionId'], values.auditData ?? {}) ?? {},
            (values.auditAssets as Record<string, AuditDataAssetsV2Body>) ?? {}
          );
        })
        // Sync battery certificate document
        .then(async () => {
          await syncDocuments({
            vehicleId,
            contextTarget: 'vehicle',
            fileFields: [
              {
                fieldValue: values.batteryCertificateDocument,
                documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
              },
            ],
          });
        })
        .then(async () => {
          if (isBusinessCaseReferrer) {
            await attachToBusinessCase(vehicleId);
          }
          navigateOnConfirm(vehicleId);
        });

    // eslint-disable-next-line no-restricted-syntax
    return onSave().catch(handleApiError);
  };

  const isCreating = isUpdating || isRegistering;
  const isLoading = isMandatoryFieldsLoading || isVehicleLoading;
  const isError = isVehicleError;

  const baseVehicle = vehicle ? toBaseVehicle(vehicle) : undefined;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.actions.createSaleVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VFKit.Form
          showMandatoryFields
          mode="onBlur"
          experimentalZodSchema={formSchema}
          defaultValues={{
            ...baseVehicle,
            targetAlbum: ['PHOTOS_CAR_PURCHASE'],
          }}
        >
          <SaleVehicleForm
            onSave={handleSave}
            onVehicleTypeChange={handleVehicleTypeChange}
            onFuelTypeChange={handleFuelTypeChange}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating}
            tabRoutePattern="createSaleVehicleFromExistingVehicleV2"
            vehicleId={vehicleId}
            modalKeys={{
              headline: i18n.t('entity.vehicle.actions.createSaleVehicle'),
            }}
            disableVinDecoder
            infoAlert={{
              title: i18n.t('entity.vehicle.labels.creatingSaleForServiceVehicleMessage'),
              message: i18n.t('entity.vehicle.labels.creatingSaleForServiceVehicle'),
            }}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
